import type { AdminAccessResponse } from '../../../../api';
import { useAdminAccess } from '../../../../hooks/useAdminAccess';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../redux/hooks';
import ROLES from '../../../../utils/roles';

/**
 * Who may open Hiring (DA-5042 section 2): an org admin of the org, a manager
 * of one of its teams, or a platform admin, AND only when the org has the
 * Hiring flag on.
 *
 * Both halves are also checked on the server (BE
 * src/service/hiring/hiringCore.ts resolveOwnerScope): this is only what keeps
 * the nav item and the routes out of the way of everyone else. The flag in
 * particular is a compliance switch, not a preview toggle: an org gets a
 * feature that assesses job candidates with AI on purpose, so an org without
 * it must not even see the section.
 */
export const canOpenHiring = (access: AdminAccessResponse | null, isPlatformAdmin: boolean, role: string): boolean => {
  if (isPlatformAdmin) return true;
  if (!access) return role === ROLES.orgAdmin;
  return access.orgAdmin || access.managedTeamIds.length > 0;
};

/** True when the org has Hiring switched on (organizations.flags.hiring). */
export const hiringEnabledForOrg = (org: { flags?: { hiring?: boolean } } | null | undefined): boolean =>
  org?.flags?.hiring === true;

/**
 * The same for the signed-in user, for the route guard and the nav: null until
 * the org and /v3/admin/access have answered, so a guard never flashes
 * /unauthorized at someone who is allowed in.
 */
export const useHiringAccessible = (): boolean | null => {
  const org = useAppSelector((state) => state.orgDetails?.data);
  const orgKnown = Boolean(org?.authId);
  const { isPlatformAdmin } = useProgramContext();
  const { access, loading } = useAdminAccess();
  if (!orgKnown) return null;
  if (!hiringEnabledForOrg(org)) return false;
  if (isPlatformAdmin) return true;
  if (loading) return null;
  return canOpenHiring(access, false, localStorage.getItem('role') || '');
};
